// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Orders / Cancel  (button + inline confirmation, used on the order list and the order page)
// Shown only while `order.canCancel` (ZenFresh hasn't confirmed the order yet). Asking first, because it can't be undone:
// POST /api/me/orders/:ref/cancel cancels the order and refunds it: the wallet part back to the wallet, the online part to
// the ORIGINAL payment method (Razorpay) or — the customer's choice — to the wallet, instantly. If ZenFresh confirmed the order a moment earlier the server refuses (409) and this says so and reloads.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef, useState } from "react";
import { Ban } from "lucide-react";
import { ApiError } from "../../utils/api";
import { fetchCustomerReasons, type PublicReason } from "../../utils/cancelReasons";
import { cancelOrder, fetchOrder, type Order } from "../../utils/orders";
import { formatInr } from "../../utils/priceFormat";

interface CancelOrderProps {
  order: Order;
  /** Called with the fresh order after a successful cancel, or after the server said it is too late. */
  onChanged: (order: Order) => void;
}

export default function CancelOrder({ order, onChanged }: CancelOrderProps) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refundTo, setRefundTo] = useState<"original" | "wallet">("original");
  const [error, setError] = useState<string | null>(null);
  const keep = useRef<HTMLButtonElement>(null);
  // Staff-kept reasons (Cancellation reasons screen). None published = nothing is asked, as before.
  const [reasons, setReasons] = useState<PublicReason[]>([]);
  const [reasonCode, setReasonCode] = useState("");
  const [note, setNote] = useState("");
  useEffect(() => {
    if (!confirming) return;
    let cancelled = false;
    fetchCustomerReasons().then((list) => !cancelled && setReasons(list), () => undefined);
    return () => { cancelled = true; };
  }, [confirming]);
  const chosen = reasons.find((r) => r.code === reasonCode);

  useEffect(() => {
    if (confirming) keep.current?.focus();
  }, [confirming]);

  if (!order.canCancel) return null;
  const cardPart = order.payment ? order.total - order.walletAmount : 0;

  const confirm = async () => {
    if (reasons.length > 0 && !chosen) { setError("Please choose a reason."); return; }
    if (chosen?.requiresNote && note.trim() === "") { setError("Please add a short note."); return; }
    setBusy(true);
    setError(null);
    try {
      onChanged(await cancelOrder(order.ref, refundTo, chosen?.code, note.trim() || undefined));
      setConfirming(false);
    } catch (e) {
      if (e instanceof ApiError && e.code === "CANCEL_NOT_ALLOWED") {
        setError("ZenFresh has just confirmed this order, so it can't be cancelled here any more. Contact us if you need to change it.");
        try {
          onChanged(await fetchOrder(order.ref));
        } catch {
          // The message above is enough; the list refreshes itself shortly.
        }
      } else {
        setError("We couldn't cancel that. Check your connection and try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  if (!confirming) {
    return (
      <>
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className="inline-flex min-h-11 items-center justify-center gap-1.5 rounded-full px-4 text-sm font-bold text-coral-500 ring-1 ring-coral-500/30 hover:bg-coral-400/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral-500 dark:text-coral-300"
        >
          <Ban className="h-4 w-4" aria-hidden="true" /> Cancel<span className="sr-only"> order {order.ref}</span>
        </button>
        {error && <p role="alert" className="basis-full text-xs font-semibold text-coral-500 dark:text-coral-300">{error}</p>}
      </>
    );
  }

  return (
    <div role="group" aria-label={`Cancel order ${order.ref}?`} className="basis-full rounded-xl bg-coral-400/10 p-3">
      <p className="text-sm font-bold text-ocean-950 dark:text-foam-50">Cancel this order?</p>
      {reasons.length > 0 && (
        <fieldset className="mt-2" data-testid="cancel-reasons">
          <legend className="text-xs font-semibold text-ocean-800/90 dark:text-foam-100/85">Why are you cancelling?</legend>
          {reasons.map((r) => (
            <label key={r.code} className="mt-1.5 flex items-start gap-2 text-sm">
              <input type="radio" name={`cancel-reason-${order.ref}`} checked={reasonCode === r.code} onChange={() => setReasonCode(r.code)} className="mt-1 h-4 w-4 accent-teal-600" />
              <span>{r.label}{r.hint && <span className="block text-xs text-ocean-800/80 dark:text-foam-100/75">{r.hint}</span>}</span>
            </label>
          ))}
          {chosen?.requiresNote && (
            <textarea aria-label="Tell us more" value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} rows={2} className="mt-2 w-full rounded-lg border border-ocean-900/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-white/5" />
          )}
        </fieldset>
      )}
      {cardPart > 0 ? (
        <fieldset className="mt-2">
          <legend className="text-xs text-ocean-800/90 dark:text-foam-100/85">Where should the {formatInr(cardPart)} you paid online go? This can&apos;t be undone.</legend>
          <label className="mt-2 flex items-start gap-2 text-sm">
            <input type="radio" name={`refund-to-${order.ref}`} checked={refundTo === "original"} onChange={() => setRefundTo("original")} className="mt-1 h-4 w-4 accent-teal-600" />
            <span><span className="font-semibold">Original payment method</span><span className="block text-xs text-ocean-800/80 dark:text-foam-100/75">Your bank decides when it shows up.</span></span>
          </label>
          <label className="mt-2 flex items-start gap-2 text-sm">
            <input type="radio" name={`refund-to-${order.ref}`} checked={refundTo === "wallet"} onChange={() => setRefundTo("wallet")} className="mt-1 h-4 w-4 accent-teal-600" />
            <span><span className="font-semibold">ZenFresh wallet</span><span className="block text-xs text-ocean-800/80 dark:text-foam-100/75">Credited instantly; use it on your next order.</span></span>
          </label>
        </fieldset>
      ) : (
        <p className="mt-1 text-xs text-ocean-800/90 dark:text-foam-100/85">This can&apos;t be undone.</p>
      )}
      {order.walletAmount > 0 && (
        <p className="mt-2 text-xs text-ocean-800/90 dark:text-foam-100/85">The {formatInr(order.walletAmount)} you paid from your wallet goes back to your wallet.</p>
      )}
      {error && <p role="alert" className="mt-2 text-xs font-semibold text-coral-500 dark:text-coral-300">{error}</p>}
      <div className="mt-3 flex gap-2">
        <button ref={keep} type="button" onClick={() => setConfirming(false)} disabled={busy} className="min-h-11 flex-1 rounded-full bg-white px-4 text-sm font-bold text-ocean-900 ring-1 ring-ocean-900/15 hover:bg-ocean-700/5 disabled:opacity-50">
          Keep order
        </button>
        <button type="button" onClick={() => void confirm()} disabled={busy} className="min-h-11 flex-1 rounded-full bg-coral-500 px-4 text-sm font-bold text-white hover:bg-coral-400 disabled:opacity-50">
          {busy ? "Cancelling…" : "Yes, cancel & refund"}
        </button>
      </div>
    </div>
  );
}
